import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import DialogContent from '@mui/material/DialogContent';
import DynamicFilters, { FilterConfig } from './DynamicFilters'; // Import DynamicFilters and its type
import { Box, Divider, List, ListItem, ListItemText, styled } from '@mui/material';
import { getDefaultFilterValue } from './custom-filter-panel.util';

// Styled components
const StyledListItem = styled(ListItem)({
  width: '100%',
  cursor: 'pointer',
  margin: '30px 0',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  '& .MuiListItemText-primary': {
    color: '#383838',
  },
});

const StyledDialogContent = styled(DialogContent)({
  overflowY: 'auto',
  height: '400px',
  paddingInline: '30px',
});

export interface CustomFiltersRef {
  clearFilters: () => void;
  applyCurrentFilters: () => void;
  handleAddFilter: (propertyName: string) => void;
  filtersNotOnDisplay: FilterConfig[];
  populateTempValues: () => void;
}

interface CustomFiltersProps {
  filtersConfig: FilterConfig[];
  onFilterChange: (filterString: any[] | '', rawFilters: any) => void;
  prevAppliedFilters: string | undefined;
  autoApplyOnChange?: boolean;
}

const CustomFilters = forwardRef<CustomFiltersRef, CustomFiltersProps>(
  ({ filtersConfig, onFilterChange, prevAppliedFilters, autoApplyOnChange = false }, ref) => {
    const [filterValues, setFilterValues] = useState<any>({});
    const [tempFilterValues, setTempFilterValues] = useState<any>({});
    const [additionalFilters, setAdditionalFilters] = useState<string[]>([]);

    const filterListRef = useRef<HTMLDivElement>(null);

    React.useImperativeHandle(ref, () => ({
      clearFilters,
      applyCurrentFilters,
      handleAddFilter: handleAddFilterFromMenu,
      filtersNotOnDisplay,
      populateTempValues,
    }));

    useEffect(() => {
      if (autoApplyOnChange) {
        applyCurrentFilters();
      }
    }, [tempFilterValues, autoApplyOnChange]);

    const resetFilter = (propertyName: string) => {
      const newTempFilterValues = {
        ...tempFilterValues,
        [propertyName]: getDefaultFilterValue(propertyName, filtersConfig),
      };
      setTempFilterValues(newTempFilterValues);
    };

    const populateTempValues = () => {
      setTempFilterValues(filterValues);
    };

    const filtersNotOnDisplay = filtersConfig.filter(config => !config.onDisplay && !additionalFilters.includes(config.propertyName));

    const handleAddFilterFromMenu = (propertyName: string) => {
      if (!additionalFilters.includes(propertyName)) {
        const newAdditionalFilters = [...additionalFilters, propertyName];

        const filterAlreadyHasValue = propertyName in tempFilterValues;
        const newTempFilterValues = filterAlreadyHasValue
          ? { ...tempFilterValues }
          : { ...tempFilterValues, [propertyName]: getDefaultFilterValue(propertyName, filtersConfig) };

        setAdditionalFilters(newAdditionalFilters);
        setTempFilterValues(newTempFilterValues);
      }
    };

    useEffect(() => {
      if (additionalFilters.length == 0) return;
      if (filterListRef.current) {
        filterListRef.current.scrollTop = filterListRef.current.scrollHeight;
      }
    }, [additionalFilters]);

    const handleFilterChange = (propertyName: string, value: any) => {
      setTempFilterValues({ ...tempFilterValues, [propertyName]: value });
    };

    const generateFilterString = (currentFilterValues: any) => {
      if (Object.keys(currentFilterValues).length === 0) {
        return '';
      }
      const singleFilterParts: any[] = [];
      const groupFilterParts: { [key: string]: any[] } = {};

      const addFilterString = (config: FilterConfig, filterString: string) => {
        if (config.group) {
          groupFilterParts[config.group] = groupFilterParts[config.group] || [];
          groupFilterParts[config.group].push(filterString);
        } else {
          singleFilterParts.push(filterString);
        }
      };

      Object.keys(currentFilterValues).forEach(key => {
        const value = currentFilterValues[key];
        const config = filtersConfig.find(c => c.propertyName === key);

        if (config) {
          switch (config.type) {
            case 'text':
              if (config.multiSelect) {
                // Filter out any empty strings
                const validValues = value.filter((v: any) => v.trim() !== '');
                if (validValues.length > 0) {
                  const isMatchParts = validValues.map((v: any) => `search.ismatch('${v}*', '${key}')`);
                  addFilterString(config, `(${isMatchParts.join(' or ')})`);
                }
              } else {
                if (value.trim() !== '') {
                  addFilterString(config, `${key} eq '${value}'`);
                }
              }
              break;
            case 'number':
              if (Array.isArray(value) && value.length === 2) {
                addFilterString(config, `${key} ge ${value[0]} and ${key} le ${value[1]}`);
              }
              break;

            case 'date': {
              const dateRange = currentFilterValues[key];
              if (dateRange) {
                const { start, end } = dateRange;
                let formattedStartDate = null;
                if (start) {
                  try {
                    formattedStartDate = new Date(start).toISOString();
                  } catch (e) {
                    console.error('Erorr in formatting start date', e);
                  }
                }
                let formattedEndDate = null;
                if (end) {
                  try {
                    formattedEndDate = new Date(end).toISOString();
                  } catch (e) {
                    console.error('Error in formatting end date', e);
                  }
                }

                if (formattedStartDate) {
                  addFilterString(config, `${key} ge ${formattedStartDate}`);
                }
                if (formattedEndDate) {
                  addFilterString(config, `${key} le ${formattedEndDate}`);
                }
              }
              break;
            }
            case 'lookup':
              if (config.multiSelect) {
                // For multi-select, filter out any invalid values and check if the array is not empty
                const validValues = Array.isArray(value) ? value.filter(v => v != null && v.toString().trim() !== '') : [];
                if (validValues.length > 0) {
                  let lookupFilterParts;
                  if (config.nested) {
                    lookupFilterParts = validValues.map(v => `${key}/any(k: k eq '${v}')`);
                  } else {
                    lookupFilterParts = validValues.map(v => `${key} eq '${v}'`);
                  }
                  addFilterString(config, `(${lookupFilterParts.join(' or ')})`);
                }
              } else {
                // For single-select, ensure the value is valid before adding
                if (value != null && value.toString().trim() !== '') {
                  addFilterString(config, `${key} eq '${value}'`);
                }
              }
              break;
            case 'boolean': {
              if (value != null) {
                addFilterString(config, `${key} eq ${value}`);
              }
              break;
            }
          }
        }
      });

      Object.keys(groupFilterParts).forEach(group => {
        const filterParts = groupFilterParts[group];
        if (filterParts.length > 0) {
          const groupFilterString = `(${filterParts.join(' or ')})`;
          singleFilterParts.push(groupFilterString);
        }
      });

      return singleFilterParts;
    };

    // Apply filters and update main states
    const applyFilters = useCallback(
      (currentFilterValues: any, currentAdditionalFilters: any) => {
        setFilterValues(currentFilterValues);
        setAdditionalFilters(currentAdditionalFilters);
        const filterString = generateFilterString(currentFilterValues);
        onFilterChange(filterString, currentFilterValues);
      },
      [onFilterChange],
    );

    const applyCurrentFilters = () => applyFilters(tempFilterValues, additionalFilters);

    // Effect to apply previous filters if they exist
    useEffect(() => {
      if (prevAppliedFilters && prevAppliedFilters != '""') {
        const parsedFilters = JSON.parse(prevAppliedFilters);
        applyFilters(parsedFilters, additionalFilters);
      }
    }, [prevAppliedFilters]);

    const clearFilters = () => {
      setTempFilterValues({});
      setFilterValues({});
      setAdditionalFilters([]);

      applyFilters({}, []);

      if (filterListRef.current) {
        filterListRef.current.scrollTop = 0;
      }
    };

    const renderConfig = (config: FilterConfig) => {
      return (
        <React.Fragment key={config.propertyName}>
          <StyledListItem>
            <Box flex={2} pr={10}>
              <ListItemText primary={config.propertyDisplayName} primaryTypographyProps={{ style: { fontWeight: 'bold' } }} />
            </Box>
            <Box flex={6}>
              <DynamicFilters
                filtersConfig={[config]}
                filterValues={tempFilterValues}
                onFilterChange={handleFilterChange}
                resetFilter={resetFilter}
              />
            </Box>
          </StyledListItem>
        </React.Fragment>
      );
    };

    const renderGroup = (groupName: string, configs: FilterConfig[]) => {
      return (
        <React.Fragment key={groupName}>
          <StyledListItem sx={{ alignItems: 'flex-start' }}>
            <Box flex={2} pr={10} pt={3}>
              <ListItemText primary={groupName} primaryTypographyProps={{ style: { fontWeight: 'bold' } }} />
            </Box>
            <Box flex={6}>
              <DynamicFilters
                filtersConfig={configs}
                filterValues={tempFilterValues}
                onFilterChange={handleFilterChange}
                resetFilter={resetFilter}
              />
            </Box>
          </StyledListItem>
        </React.Fragment>
      );
    };

    const renderFilters = (filters: FilterConfig[]) => {
      const singleFilters = filters.filter(config => config.group == undefined);
      const groupFilters = filters.filter(config => config.group != undefined);
      const groups = groupFilters.reduce((groups: Record<string, FilterConfig[]>, filter) => {
        const groupName = filter.group || 'Default';
        if (!groups[groupName]) {
          groups[groupName] = [];
        }
        groups[groupName].push(filter);
        return groups;
      }, {});

      return (
        <>
          {singleFilters.map(config => renderConfig(config))}
          {Object.entries(groups).map(([groupName, groupFilters], index) => renderGroup(groupName, groupFilters))}
        </>
      );
    };

    const renderAlwaysDisplayFilters = () => {
      let onDisplayFilters = filtersConfig.filter(config => config.onDisplay);
      const onDisplayGroups = new Set<string>();
      onDisplayFilters.forEach(config => {
        if (config.group) {
          onDisplayGroups.add(config.group);
        }
      });
      const additionalFiltersConfigs = additionalFilters
        .map(propertyName => {
          const config = filtersConfig.find(conf => conf.propertyName === propertyName);
          return config;
        })
        .filter((item): item is FilterConfig => item !== undefined)
        .filter(config => !config.onDisplay);
      const additionalFiltersInOnDisplayGroups = additionalFiltersConfigs.filter(config => onDisplayGroups.has(config.group ?? ''));
      onDisplayFilters = onDisplayFilters.concat(additionalFiltersInOnDisplayGroups);
      return renderFilters(onDisplayFilters);
    };
    const renderAdditionalFilters = () => {
      let additionalFiltersConfigs = additionalFilters
        .map(propertyName => {
          const config = filtersConfig.find(conf => conf.propertyName === propertyName);
          return config;
        })
        .filter((item): item is FilterConfig => item !== undefined);

      const onDisplayFilters = filtersConfig.filter(config => config.onDisplay);
      const onDisplayGroups = new Set<string>();
      onDisplayFilters.forEach(config => {
        if (config.group) {
          onDisplayGroups.add(config.group);
        }
      });

      additionalFiltersConfigs = additionalFiltersConfigs.filter(config => !onDisplayGroups.has(config.group ?? ''));

      return renderFilters(additionalFiltersConfigs);
    };

    const renderFilterList = () => (
      <List dense>
        {renderAlwaysDisplayFilters()}
        <Divider />
        {renderAdditionalFilters()}
      </List>
    );

    return <StyledDialogContent ref={filterListRef}>{renderFilterList()}</StyledDialogContent>;
  },
);

CustomFilters.displayName = 'CustomFilters';

export default CustomFilters;
